/**
 * Which draft room a league gets — rule (e) of the routing cutover.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *
 *   pending      -> new     the draft has not started; nothing to disrupt
 *   in_progress  -> legacy  FINISH WHERE YOU STARTED
 *   paused       -> legacy  a paused draft is still a draft in flight
 *   completed    -> new     it is over; the new room reads it better
 *
 * Keyed on DRAFT_STATUS, not on when the league was created. A creation-date cutoff
 * would put a league drafted the day after the flip into the new room mid-draft, which is
 * the one outcome this rule exists to prevent. Status is the property that actually
 * determines whether anyone is mid-draft right now.
 *
 * ── WHY "FINISH WHERE YOU STARTED" IS THE LOAD-BEARING HALF ─────────────────
 *
 * A draft is a timed, real-money-adjacent event. Someone who opened a room, learned where
 * the queue toggle and the pick button are, and is watching a clock must not have the
 * interface changed underneath them between one pick and the next -- a deploy would
 * otherwise do exactly that, mid-draft, with a countdown running. Autodraft takes the pick
 * if they hesitate, so the cost of a moment's confusion is a real one.
 *
 * ── DEFAULT ────────────────────────────────────────────────────────────────
 *
 * Anything else -- null, cancelled, a status added server-side that this file has not
 * learned -- resolves to the new room, because the new room is canonical and only
 * in_progress/paused describe a draft in flight. An unknown status is not a reason to send
 * someone to a room that is on its way out.
 */

export type DraftRoom = 'new' | 'legacy';

/**
 * ── SAB-931: THE RULE ABOVE WAS THE DEFECT, AND THIS REPLACES IT ────────────
 *
 * `roomForDraftStatus` keyed the room on `draft_status`, which is EXACTLY the value that
 * changes when a draft starts. So the rule written to stop anyone's room changing mid-draft
 * GUARANTEED a room change at the one moment it most mattered: press Start Draft in V2, and
 * the next render serves legacy. Joe Bond hit it on 2026-08-30.
 *
 * Nobody chose that. It fell out of the discriminator, and it survived unnoticed because
 * until SAB-927 shipped -- three hours earlier -- no draft could be started from the UI at
 * all, so the boundary had never been crossed.
 *
 * ── THE FIX IS A PIN THAT NOTHING ELSE CAN MOVE ─────────────────────────────
 *
 * "Pick better statuses" is not the fix. ANY discriminator that can change while someone is
 * sitting in the room reintroduces the bug. The room must be decided once, at draft start,
 * and then be immovable.
 *
 * `nfl_leagues.draft_room` is that decision, written by nfl-initialize-draft and frozen by a
 * trigger the moment it is set.
 *
 * ── WHY NOT draft_start_time, WHICH ALREADY EXISTED ─────────────────────────
 *
 * It was the obvious candidate -- already a typed column, already written at draft start,
 * no migration -- and it is WRONG, for two reasons found by checking rather than assuming:
 *
 *   1. It is COMMISSIONER-WRITABLE mid-draft. `nfl-update-league` lists it in
 *      ALLOWED_FIELDS, and the SAB-907 guard only freezes it once draft_status is
 *      'completed'. So during a live draft a commissioner editing the draft date would move
 *      themselves and everyone else between rooms -- the original defect, new trigger.
 *   2. It is SEMANTICALLY OVERLOADED. nfl-create-league:213 writes it as the SCHEDULED
 *      draft date; initialize later overwrites it with the ACTUAL start. One column, two
 *      meanings, and the pin would depend on which one it currently held.
 *
 * A dedicated column cannot be lost by a wholesale `settings` PATCH either -- the standing
 * hazard that has dropped the deadline, pick pointer and pause state together before.
 *
 * ── THE CUTOVER HAS HAPPENED ───────────────────────────────────────────────
 *
 * nfl-initialize-draft now pins new drafts to 'v2'. It pinned 'legacy' while V2 could not
 * serve a live draft -- 5 of 7 sabotage effects had no UI there, and two (hide_info,
 * league_guess_who) are ENTIRELY client-rendered, so a player would have paid for a card
 * the target never saw. That gap closed (banners, Guess Who masking, the player detail
 * modal), which is what allowed the literal to move.
 *
 * Shipping the mechanism first, pinned to 'legacy', was the point: the pin had to already
 * exist at cutover, or the drafts in flight at that moment would have had nothing to be
 * pinned by. They did, so they kept legacy and finished there.
 *
 * WHAT IS STILL LOAD-BEARING HERE: the `roomForDraftStatus` fallback below. Leagues that
 * started drafting before the pin shipped have draft_room = NULL and are NOT covered by the
 * cutover -- the fallback is the only thing keeping them in legacy. Do not delete it, or
 * simplify `roomForLeague` to "always new", until every unpinned in-flight draft has
 * finished. Check with:
 *   select count(*) from nfl_leagues
 *    where draft_room is null and draft_status in ('in_progress','paused');
 */

/** The value nfl-initialize-draft stamps. 'legacy' | 'v2'; null means never pinned. */
export type PinnedDraftRoom = 'legacy' | 'v2';

export interface DraftRoomLeague {
  draft_status?: string | null;
  /** SAB-931: the pin, written once at draft start and frozen by trigger. */
  draft_room?: string | null;
}

/**
 * Which room this league gets.
 *
 * ── SAB-1010: UNPINNED MEANS V2, AT EVERY STATUS ────────────────────────────
 *
 * The pin still wins whenever it is set -- a pinned draft keeps its room through
 * in_progress, paused and completed alike, so no transition can move it. What changed is
 * the FALLBACK for unpinned leagues.
 *
 * It used to defer to roomForDraftStatus, which sent in_progress and paused to legacy. That
 * was correct when it shipped: those were drafts started before the V2 cutover, and the
 * fallback let them finish where they began. It has now outlived that purpose. Everything
 * SAB-990/991/996/997 hardened -- instant flagged autopick, queue clearing and live
 * refresh, virtualization, the alt-tab fix, correct roster denominators -- lives only in
 * V2, so an unpinned league landing in legacy gets none of it.
 *
 * Measured before the change: exactly ONE prod league took the legacy path (Joe Test,
 * paused, a test league last touched two days earlier) and ZERO leagues on either lineage
 * are pinned 'legacy'. So this moves one test draft and nothing else.
 *
 * The explicit 'legacy' pin is DELIBERATELY still honoured. Nothing sets it today, which is
 * precisely why it is worth keeping: it is the rollback lever. nfl-initialize-draft stamps
 * the literal 'v2', and reverting the cutover means changing that literal back -- which only
 * works if something downstream still reads 'legacy'.
 */
export function roomForLeague(league: DraftRoomLeague | null | undefined): DraftRoom {
  const pin = league?.draft_room;
  if (pin === 'legacy') return 'legacy';
  // Everything else -- 'v2', null, and any unrecognised value -- gets V2. Junk is treated as
  // unpinned rather than trusted, and unpinned now means V2.
  return 'new';
}



/** The two statuses that mean a draft is happening right now. */
export const IN_FLIGHT_STATUSES: ReadonlySet<string> = new Set(['in_progress', 'paused']);

/**
 * @deprecated SAB-931, and as of SAB-1010 no longer reachable from `roomForLeague` at all.
 *
 * Keyed on `draft_status`, the very value that changes when a draft starts, so it moved a
 * drafter between rooms at exactly the wrong moment. Retained ONLY so the historical
 * behaviour stays pinned by its own tests -- deleting it would delete the record of what
 * the routing used to do, which is what the next person will want when they read a league
 * that drafted in legacy. No production code may call it.
 */
export function roomForDraftStatus(status: string | null | undefined): DraftRoom {
  return typeof status === 'string' && IN_FLIGHT_STATUSES.has(status) ? 'legacy' : 'new';
}
